import { useEffect, useState, useCallback } from "react";
import { useSuperChatLogStore } from "@/store/useSuperChatLogStore";

/**
 * SuperChatLogSessionList 커스텀 훅
 *
 * @description 채팅 로그 세션 리스트의 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const useSuperChatLogSessionList = () => {
  const {
    sessions,
    loading,
    error,
    pagination,
    filters,
    fetchSessions,
    fetchSessionMessages,
    exportToExcel,
    setFilters,
    setPagination,
    clearError,
    reset,
  } = useSuperChatLogStore();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [selectedSession, setSelectedSession] =
    useState<{
      sessionUuid: string;
      sessionId: string;
      sessionType: string;
      sessionStatus: string;
      totalMessages: number;
      startedAt: string;
      lastActivityAt: string;
    } | null>(null);
  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);

  /**
   * 세션 목록 로드
   */
  const loadSessions = useCallback(async () => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    };

    await fetchSessions(params);
  }, [pagination.page, pagination.limit, filters, fetchSessions]);

  // 컴포넌트 마운트 시 Store 상태 초기화
  useEffect(() => {
    // Store 상태를 완전히 초기화하여 일관성 보장
    reset();
  }, [reset]); // reset 함수가 변경되지 않으므로 한 번만 실행

  // 페이지네이션 및 필터 변경 시 데이터 로드
  useEffect(() => {
    loadSessions();
  }, [pagination.page, pagination.limit, filters, loadSessions]);

  // 에러 처리
  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarOpen(true);
      clearError();
    }
  }, [error, clearError]);

  /**
   * 페이지 변경 처리
   */
  const handlePageChange = (newPage: number) => {
    setPagination({ page: newPage });
  };

  /**
   * 페이지 크기 변경 처리
   */
  const handleLimitChange = (newLimit: number) => {
    setPagination({ page: 1, limit: newLimit });
  };

  /**
   * 필터 변경 처리
   */
  const handleFilterChange = (newFilters: {
    sessionType?: string;
    sessionUserType?: string;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    setFilters(newFilters);
    setPagination({ page: 1 }); // 필터 변경 시 첫 페이지로
  };

  /**
   * 필터 초기화
   */
  const handleResetFilters = () => {
    setFilters({});
    setPagination({ page: 1 });
  };

  /**
   * 새로고침
   */
  const handleRefresh = () => {
    loadSessions();
  };

  /**
   * Excel 내보내기 처리
   */
  const handleExport = async () => {
    try {
      const blob = await exportToExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `chat-sessions-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbarMessage("세션 목록이 Excel 파일로 내보내기되었습니다.");
      setSnackbarOpen(true);
    } catch {
      // 에러는 스토어에서 처리됨
    }
  };

  /**
   * 세션 상세 보기
   */
  const handleViewSession = async (session: {
    sessionUuid: string;
    sessionId: string;
    sessionType: string;
    sessionStatus: string;
    totalMessages: number;
    startedAt: string;
    lastActivityAt: string;
  }) => {
    try {
      setSelectedSession(session);
      setSessionDetailOpen(true);

      // 세션 메시지 로드
      await fetchSessionMessages(session.sessionUuid);
    } catch {
      setSnackbarMessage("세션 상세 정보를 불러오는데 실패했습니다.");
      setSnackbarOpen(true);
    }
  };

  /**
   * 세션 상세 모달 닫기
   */
  const handleCloseSessionDetail = () => {
    setSessionDetailOpen(false);
    setSelectedSession(null);
  };

  /**
   * 스낵바 닫기 처리
   */
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return {
    // 상태
    sessions,
    loading,
    error,
    pagination,
    filters,
    snackbarOpen,
    snackbarMessage,
    selectedSession,
    sessionDetailOpen,

    // 핸들러
    handlePageChange,
    handleLimitChange,
    handleFilterChange,
    handleResetFilters,
    handleRefresh,
    handleExport,
    handleViewSession,
    handleCloseSessionDetail,
    handleSnackbarClose,
  };
};
